import React, { useEffect, useMemo } from 'react';
import { makeStyles } from '@material-ui/styles';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useLazyQuery } from '@apollo/client';
import { Tooltip } from 'antd';
import {
  AllBook as BookingTotalSvg,
  CompleteBook as BookingCompleteSvg,
  OrderRate as BookingRateSvg,
} from '~components/svgs';
import { BorderAdd as Add } from '~components/svgs';
import generatePath from '~settings/routing';
import { useHistory } from 'react-router-dom';
import { QUERY_COUNT_DASHBORD } from './query';
import { BoardCircle as QuestionCircleOutlined } from '~components/svgs';
import formatPrice from '~helpers/currency';

const useStyle = makeStyles(theme => ({
  broadCastBox: {
    background: theme.colors.gray1,
    padding: '0 24px',
    marginTop: 16,
  },
  bookingContent: {
    ...theme.flex.crossCenter,
    paddingBottom: 20,
    borderBottom: '1px solid rgba(0, 0, 0, 0.09)',
  },

  title: {
    padding: '17px 0',
    ...theme.typography.h5,
    color: theme.colors.titleColor,
  },
  totalProperties: {
    ...theme.typography.h1,
    color: theme.colors.titleColor,
  },
  addSvg: {
    width: 12,
    marginRight: 10,
    display: 'inline-block',
    fill: 'rgba(0, 0, 0, 0.65)',
    verticalAlign: -2,
  },
  add: {
    padding: '5px 16px',
    color: theme.colors.bodyColor,
    ...theme.typography.body2,
    border: '1px solid #D9D9D9',
    cursor: 'pointer',
    borderRadius: 2,
    height: 32,
    transition: 'all 0.3s ease',
    ...theme.flex.crossCenter,
    '&:hover': {
      color: '#5abfb2',
      borderColor: '#5abfb2',
      '& svg': {
        fill: 'rgba(90,191,178,1)',
        fillOpacity: 1,
      },
    },
  },
  createBox: {
    ...theme.flex.mainSpaceBetween,
    ...theme.flex.crossCenter,
    paddingBottom: 16,
    borderBottom: '1px solid rgba(0, 0, 0, 0.09)',
  },
  countContent: {
    ...theme.flex.mainSpaceBetween,
    ...theme.flex.crossCenter,
  },
  currencyBox: {
    marginTop: 8,
  },
  publish: {
    marginTop: 16,
    marginBottom: 8,
  },
  unPublish: {
    paddingBottom: 24,
  },
  list: {
    ...theme.typography.body2,
    fontWeight: 400,
  },
  circle: {
    display: 'inline-block',
    width: 8,
    height: 8,
    marginRight: 8,
    borderRadius: '50%',
    background: theme.colors.mainGreen6,
  },
  unpublishCircle: {
    background: theme.colors.gray6,
  },
  num: {
    ...theme.typography.h5,
    color: theme.colors.titleColor,
  },
  time: {
    ...theme.typography.body4,
    fontWeight: 400,
    color: theme.colors.disableColor,
  },
  rightContent: {
    marginLeft: 20,
  },
  number: {
    ...theme.typography.h2,
    color: theme.colors.titleColor,
  },
  desc: {
    ...theme.typography.body5,
    fontWeight: 400,
    color: theme.colors.captionColor,
  },
  png: {
    width: 24,
    height: 24,
    display: 'inline-block',
  },
  bookingContentBottom: {
    paddingTop: 20,
    paddingBottom: 32,
    marginBottom: 0,
    border: 'none',
  },
  currencybox: {
    background: '#FFF1F0',
    borderRadius: 2,
    padding: '11px 24px 16px 24px',
  },
  currencyTitle: {
    ...theme.typography.body2,
    fontWeight: 400,
    color: theme.colors.bodyColor,
  },
  subTitle: {
    ...theme.typography.body5,
    color: theme.colors.captionColor,
  },
  totoalMoney: {
    ...theme.typography.h5,
    color: theme.colors.titleColor,
  },
  completeContent: {
    paddingTop: 20,
  },
  addpadding: {
    paddingBottom: 24,
  },
  totalSvg: {
    background: '#FFFBE6',
    borderRadius: 4,
    height: 40,
    padding: 8,
  },
  completSvg: {
    background: '#E4F2EE',
    borderRadius: 4,
    height: 40,
    padding: 8,
  },
  rateSvg: {
    background: '#F0F7FF',
    borderRadius: 4,
    height: 40,
    padding: 8,
  },
}));
const AboutPropertyAndBooking = () => {
  const year = new Date().getFullYear();
  const startDate = `${year}-01-01`;
  const classes = useStyle();
  const history = useHistory();
  const { t } = useTranslation();
  const [getCountDashboard, { loading, error, data }] = useLazyQuery(QUERY_COUNT_DASHBORD, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    variables: { input: { bookingJourney: 'SEMI_AUTOMATIC', startDate } },
  });

  useEffect(() => {
    getCountDashboard();
  }, []);

  const countInfo = useMemo(() => {
    const { countDashboardData = { orderData: [], propertyData: [] } } = data || {};
    const { orderData, propertyData, currencyData } = countDashboardData;
    const totalOrder = orderData?.map(i => i.orderCount).reduce((a, b) => a + b, 0);
    const pub = propertyData.filter(i => i.status === 'PUBLISHED');
    const totalPub = pub[0]?.propertyCount ? pub[0]?.propertyCount : 0;

    const unPub = propertyData.filter(i => i.status === 'UNPUBLISHED');
    const totalUnpub = unPub[0]?.propertyCount ? unPub[0].propertyCount : 0;
    const totalProperty = parseInt(totalPub) + parseInt(totalUnpub);
    const completOrderTotal = orderData
      ?.filter(i => i.stage === 'COMPLETED')
      .map(i => i.orderCount)
      .reduce((a, b) => a + b, 0);
    const rate = totalOrder === 0 ? 0 : completOrderTotal / totalOrder;
    return {
      orderData,
      propertyData,
      totalOrder,
      totalProperty,
      totalPub,
      totalUnpub,
      completOrderTotal,
      currencyData,
      rate,
    };
  }, [data]);

  return (
    <>
      <div className={classes.broadCastBox}>
        <div className={classes.title}>{t('board.right_content.propert_count.title')}</div>

        <div className={classes.createBox}>
          <div className={classes.totalProperties}>{countInfo?.totalProperty}</div>
          <div
            className={classes.add}
            onClick={() => {
              history.push(generatePath('property.create'));
            }}
          >
            <Add className={classes.addSvg} />
            {t('landlord.withdrawal.create.button.create')}
          </div>
        </div>

        <div className={`${classes.countContent} ${classes.publish}`}>
          <div className={classes.list}>
            <span className={classes.circle}></span>
            {t('cms:cms.gallery.filter_menu.item.published')}
          </div>
          <div className={classes.num}>{countInfo?.totalPub}</div>
        </div>

        <div className={`${classes.countContent} ${classes.unPublish}`}>
          <div className={classes.list}>
            <span className={`${classes.circle} ${classes.unpublishCircle}`} />
            {t('board.right_content.unpublish.title')}
          </div>
          <div className={classes.num}>{countInfo?.totalUnpub}</div>
        </div>
      </div>
      <div
        className={classNames(classes.broadCastBox, {
          [classes.addpadding]: !!countInfo?.currencyData?.length,
        })}
      >
        <div className={classes.countContent}>
          <div className={classes.title}>{t('landlord.long_tail.booking.list.title')}</div>
          <div className={classes.time}>{t('board.right_content.since.time')}</div>
        </div>
        <div className={classes.bookingContent}>
          <div className={classes.totalSvg}>
            <BookingTotalSvg className={classes.png} />
          </div>

          <div className={classes.rightContent}>
            <div className={classes.number}>{countInfo?.totalOrder}</div>
            <div className={classes.desc}>
              <label>
                {t('board.right_content.order.title')}
                <Tooltip
                  placement="top"
                  overlayStyle={{
                    width: 240,
                    zIndex: '10000000',
                    fontSize: '14px',
                    lineHeight: '22px',
                  }}
                  title={
                    <>
                      <div style={{ fontWeight: 600 }}>{t('board.right_content.order.title')}</div>
                      <div>{t('board.right_content.order.hover.dec')}</div>
                    </>
                  }
                >
                  <QuestionCircleOutlined className="long-tail__initial-setup__label-must_pay_icon" />
                </Tooltip>
              </label>
            </div>
          </div>
        </div>
        <div className={classNames(classes.bookingContent, classes.completeContent)}>
          <div className={classes.completSvg}>
            <BookingCompleteSvg className={classes.png} />
          </div>

          <div className={classes.rightContent}>
            <div className={classes.number}>{countInfo?.completOrderTotal}</div>
            <div className={classes.desc}>
              <label>
                {t('board.right_content.complete.order.title')}
                <Tooltip
                  placement="top"
                  overlayStyle={{
                    width: 240,
                    zIndex: '10000000',
                    fontSize: '14px',
                    lineHeight: '22px',
                  }}
                  title={
                    <>
                      <div style={{ fontWeight: 600 }}>
                        {t('board.right_content.complete.order.title')}
                      </div>
                      <div>{t('board.right_content.complete.order.dec')}</div>
                    </>
                  }
                >
                  <QuestionCircleOutlined className="long-tail__initial-setup__label-must_pay_icon" />
                </Tooltip>
              </label>
            </div>
          </div>
        </div>
        <div className={`${classes.bookingContent} ${classes.bookingContentBottom} `}>
          <div className={classes.rateSvg}>
            <BookingRateSvg className={classes.png} />
          </div>

          <div className={classes.rightContent}>
            <div className={classes.number}>{`${Math.round(countInfo.rate * 10000) / 100.0}%`}</div>
            <div className={classes.desc}>
              <label>
                {t('board.right_content.order_rate.title')}
                <Tooltip
                  placement="top"
                  overlayStyle={{
                    width: 240,
                    zIndex: '10000000',
                    fontSize: '14px',
                    lineHeight: '22px',
                  }}
                  title={
                    <>
                      <div style={{ fontWeight: 600 }}>
                        {t('board.right_content.order_rate.title')}
                      </div>
                      <div>{t('board.right_content.order_rate.hover.dec')}</div>
                    </>
                  }
                >
                  <QuestionCircleOutlined className="long-tail__initial-setup__label-must_pay_icon" />
                </Tooltip>
              </label>
            </div>
          </div>
        </div>
        <If condition={!!countInfo?.currencyData?.length}>
          <div className={classes.currencybox}>
            <div className={classes.subTitle}>{t('board.right_content.currency.title')}</div>

            <For each="item" of={countInfo?.currencyData} index="index">
              <div className={classNames(classes.countContent, classes.currencyBox)}>
                <div className={classes.currencyTitle}>{item?.currency}</div>
                <div className={classes.totoalMoney}>
                  {' '}
                  {formatPrice(item?.totalPrice, item?.currency)}
                </div>
              </div>
            </For>
          </div>
        </If>
      </div>
    </>
  );
};

export default AboutPropertyAndBooking;
